import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Grid, Divider, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import ProfileImageUpload from '../components/ProfileImageUpload';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import KompetenceVisualizer from '../components/KompetenceVisualizer';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  RadarController,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
// Importerer ikoner
import SpeedIcon from '@mui/icons-material/Speed';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MicIcon from '@mui/icons-material/Mic';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
// ADD NEW ICON IMPORTS
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
// Importer logo fra assets
import voiceVictorLogo from '../assets/Voice victor_png_logo_854x854.png';
// Importer realtidsfunktion og typer
import { listenToUserCalls, CallData, UserMetadata } from '../services/voiceAnalytics';
import LoadingScreen from '../components/LoadingScreen.js';

// Registrer Chart.js komponenter
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  RadarController,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Styled components
const AppContainer = styled(Box)({
  display: 'flex',
  height: '100%',
  backgroundColor: '#f8f9fa'
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '150px',
  backgroundColor: 'white',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
  boxShadow: '0 0 15px rgba(0,0,0,0.05)',
}));

const MenuItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    color: theme.palette.primary.main,
  },
  transition: 'all 0.2s ease',
}));

const MenuItemText = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
  transition: 'color 0.2s ease',
});

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  backgroundColor: '#f8f9fa',
  overflow: 'auto',
  height: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  color: '#334155',
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 5),
  maxWidth: '1400px',
  margin: '0 auto',
}));

// Sofistikeret feedback-indramning
const FeedbackContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3.5, 4.5),
  marginBottom: theme.spacing(4),
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #1a237e 0%, #283593 30%, #303f9f 50%, #3949ab 75%, #3f51b5 100%)',
  boxShadow: '0 10px 20px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.12)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 14px 26px rgba(0,0,0,0.1), 0 8px 8px rgba(0,0,0,0.1)',
  },
}));

// Subtil glød-effekt til feedbackcontainer
const GlowEffect = styled(Box)({
  position: 'absolute',
  top: '20%',
  right: '-10%',
  width: '180px',
  height: '180px',
  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
  borderRadius: '50%',
  filter: 'blur(30px)',
  opacity: 0.6,
  animation: 'pulse 10s infinite alternate ease-in-out',
  '@keyframes pulse': {
    '0%': { opacity: 0.4, transform: 'scale(1)' },
    '100%': { opacity: 0.7, transform: 'scale(1.2)' },
  },
});

// Subtilt mønster for at tilføje dybde
const PatternOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  pointerEvents: 'none',
  opacity: 0.4,
});

// Elegant brødtekst
const FeedbackText = styled(Typography)({
  fontSize: '1.15rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: 'white',
  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  position: 'relative',
  zIndex: 3,
});

// Sofistikeret baggrund til dashboardet
const DashboardBackground = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at 80% 10%, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0) 50%), radial-gradient(circle at 20% 90%, rgba(66, 165, 245, 0.01) 0%, rgba(66, 165, 245, 0) 50%)',
  zIndex: 0,
});

// Elegant indholdscontainer
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3.5, 4),
  maxWidth: '1300px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 1,
}));

// Raffineret sektion
const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
}));

// NEW STYLED COMPONENTS FOR QUESTION SECTION
const CountBadge = styled(Box)<{ bgcolor: string }>(({ bgcolor, theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: bgcolor,
  color: '#fff',
  borderRadius: '20px',
  padding: '5px 14px',
  fontSize: '1rem',
  fontWeight: 700,
  boxShadow: `0 2px 6px ${bgcolor}66`,
}));

const QuestionRow = styled(Box)<{ bgcolor: string }>(({ bgcolor, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  backgroundColor: bgcolor,
  borderRadius: '8px',
  marginBottom: theme.spacing(1.5),
  columnGap: theme.spacing(2),
}));

const NumberBubble = styled(Box)<{ bgcolor: string }>(({ bgcolor, theme }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  backgroundColor: bgcolor,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.85rem',
  fontWeight: 600,
  flexShrink: 0,
}));

const MoreLink = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  marginTop: theme.spacing(1),
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// Sofistikeret analysekort
const AnalysisCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderRadius: '12px',
  boxShadow: '0 6px 16px rgba(0,0,0,0.03), 0 1px 3px rgba(0,0,0,0.01)',
  height: '100%',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  backgroundColor: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.05), 0 2px 5px rgba(0,0,0,0.03)',
  },
}));

// Subtil skygge
const BorderHighlight = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '4px',
  background: 'linear-gradient(90deg, rgba(25, 118, 210, 0.8) 0%, rgba(66, 165, 245, 0.2) 100%)',
  opacity: 0,
  transition: 'opacity 0.25s ease',
  '$:hover > &': {
    opacity: 1,
  },
});

// Raffineret sektionsoverskrift
const SectionTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '14px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '18px',
    background: 'linear-gradient(180deg, #1976d2, #42a5f5)',
    borderRadius: '2px',
  },
});

// Elegant overskrift med ikon
const HeadingWithIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '14px',
  '& svg': {
    marginRight: '10px',
    color: '#1976d2',
  },
});

// Forfinet procentindikator
const PercentIndicator = styled(Box)({
  fontSize: '0.8rem',
  color: 'white',
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: '16px',
  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
  boxShadow: '0 2px 6px rgba(25, 118, 210, 0.2)',
});

// Statistik container til værdierne
const StatRowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '24px',
});

// Elegant ikon
const StatCircleIcon = styled(Box)({
  width: '38px',
  height: '38px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(25, 118, 210, 0.08)',
  color: '#1976d2',
  marginRight: '16px',
});

// Indhold til statistik
const StatisticsContent = styled(Box)({
  flexGrow: 1,
});

// Forfinet fremhævningsbox
const HighlightBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  border: '1px solid rgba(66, 165, 245, 0.15)',
  backgroundColor: 'rgba(66, 165, 245, 0.03)',
  marginBottom: theme.spacing(2),
}));

// Forfinet overskrift
const CardHeading = styled(Typography)({
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: '12px',
});

// Genimplementering af manglende komponenter
// Forbedret statistik bjælke
const BarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '10px',
  borderRadius: '5px',
  position: 'relative',
  backgroundColor: 'rgba(0,0,0,0.04)',
  overflow: 'hidden',
  margin: '8px 0',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
}));

// Statistikfylding
const BarFill = styled(Box)<{ fillpercentage: number }>(({ fillpercentage }) => ({
  position: 'absolute',
  left: 0,
  width: `${fillpercentage}%`,
  height: '100%',
  background: 'linear-gradient(90deg, #64b5f6 0%, #1976d2 100%)',
  borderRadius: '5px',
  transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
}));

// Premium afspilningsknap
const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(25, 118, 210, 0.08)',
  width: '32px',
  height: '32px',
  color: '#1976d2',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.15)',
    transform: 'scale(1.05)',
  }
}));

// Premium sample item
const SampleItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid rgba(0, 0, 0, 0.04)',
  padding: theme.spacing(1.6, 2),
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  backgroundColor: 'white',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(3px)',
    boxShadow: '0 3px 8px rgba(0,0,0,0.06)',
    borderColor: 'rgba(25, 118, 210, 0.1)',
  }
}));

// "Se mere" knap
const SeeMoreButton = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.85rem',
  color: '#1976d2',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '6px 0',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
    transform: 'translateY(-1px)',
  },
  '& svg': {
    fontSize: '0.95rem',
    marginLeft: '6px',
    transition: 'transform 0.2s ease',
  },
  '&:hover svg': {
    transform: 'translateX(3px)',
  }
});

// Ny tom boks til fremtidigt indhold
const EmptyPlaceholderCard = styled(AnalysisCard)(({ theme }) => ({
  minHeight: '150px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  border: '1px dashed rgba(25, 118, 210, 0.2)',
  '&:hover': {
    borderColor: 'rgba(25, 118, 210, 0.3)',
  }
}));

// Forenklet talk to listen ratio visualisering
const TalkListenRatioBox = styled(AnalysisCard)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(3),
}));

const RatioMeter = styled(Box)<{ ratio: number }>(({ ratio, theme }) => ({
  position: 'relative',
  width: '100%',
  height: '8px',
  backgroundColor: 'rgba(0,0,0,0.05)',
  borderRadius: '4px',
  margin: '20px 0 10px',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${Math.min(100, ratio * 100)}%`,
    background: 'linear-gradient(90deg, #64b5f6 0%, #1976d2 100%)',
    borderRadius: '4px',
    transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    height: '100%',
    width: '1px',
    background: 'rgba(255, 255, 255, 0.75)',
    transform: 'translateX(-50%)',
    zIndex: 1,
  }
}));

const RatioLabel = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  color: '#334155',
  fontSize: '1.1rem',
  '& svg': {
    marginRight: '10px',
    color: '#1976d2',
  }
});

const RatioValue = styled(Typography)({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#1976d2',
  lineHeight: 1.2,
});

const RadarContainer = styled(Box)({
  height: '340px',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const AgentDashboard: React.FC = () => {
  const { currentUser, userName } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for de forskellige metrikker og data
  const [latestCallData, setLatestCallData] = useState<CallData | null>(null);
  const [voiceScoreHistory, setVoiceScoreHistory] = useState<Array<{ date: string; score: number }>>([]);

  // Udvidede state-variable for hver metrik, der vises på dashboardet
  const [dominanceDistance, setDominanceDistance] = useState<number | null>(null);
  const [pitchStdDistance, setPitchStdDistance] = useState<number | null>(null);
  const [energyStdDistance, setEnergyStdDistance] = useState<number | null>(null);
  const [tlrDistance, setTlrDistance] = useState<number | null>(null);
  
  const [dominanceMean, setDominanceMean] = useState<number | null>(null);
  const [pitchStd, setPitchStd] = useState<number | null>(null);
  const [energyStd, setEnergyStd] = useState<number | null>(null);
  const [speechRateMean, setSpeechRateMean] = useState<number | null>(null);
  const [tlrValue, setTlrValue] = useState<number | null>(null);

  const [feedbackText, setFeedbackText] = useState<string>('');
  const [actionPunkt1, setActionPunkt1] = useState<string>('');
  const [actionPunkt2, setActionPunkt2] = useState<string>('');
  const [actionPunkt3Observed, setActionPunkt3Observed] = useState<string>('');
  const [actionPunkt4Observed, setActionPunkt4Observed] = useState<string>('');
  
  // State for spørgsmålsdata fra Firebase
  const [hvQuestions, setHvQuestions] = useState<string[]>([]);
  const [hvQuestionsCount, setHvQuestionsCount] = useState<number>(0);
  const [partialAccepts, setPartialAccepts] = useState<string[]>([]);
  const [partialAcceptsCount, setPartialAcceptsCount] = useState<number>(0);

  // Den nye useEffect hook for realtidsopdateringer
  useEffect(() => {
    if (!currentUser) {
      setError("Ingen bruger logget ind");
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = listenToUserCalls(currentUser.uid, (calls, userContext) => {
      setLoading(false);
      if (userContext === null && calls.length === 0) {
        // Dette kan ske hvis getUserMetadata fejler i listenToUserCalls
        setError("Kunne ikke hente brugeroplysninger for realtidsdata.");
        setDebugInfo({ error: "UserContext null, no calls", authId: currentUser.uid });
        return;
      }

      if (userContext && userContext.user_id === undefined){
        console.error("DEBUG AgentDashboard: UserMetadata mangler user_id", userContext);
        setError("Brugerkonfiguration er ufuldstændig for realtidsdata.");
        // Overvej at logge userContext for yderligere debugging
        return;
      }

      if (calls.length > 0) {
        // Sorter opkald efter dato (nyeste først) for at finde det seneste
        const sortedCalls = [...calls].sort((a, b) => b.date.getTime() - a.date.getTime());
        const latestCall = sortedCalls[0];
        setLatestCallData(latestCall);
        processAndSetDashboardData(latestCall, userContext); // userContext kan være null her, håndter i processAndSetDashboardData

        // Opdater voice score historik
        const newVoiceScoreHistory = sortedCalls
          .filter(call => call.voice_score !== undefined)
          .slice(0, 10) // Tag de seneste 10 med score
          .map(call => ({
            date: call.date.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit' }),
            score: call.voice_score!,
          }))
          .reverse(); // Nyeste til højre på grafen
        setVoiceScoreHistory(newVoiceScoreHistory);
        setError(null); // Ryd eventuelle tidligere fejl
      } else {
        // Ingen opkaldsdata endnu, nulstil evt. visninger
        setLatestCallData(null);
        processAndSetDashboardData(null, userContext);
        setVoiceScoreHistory([]);
        // Sæt ikke en fejl her nødvendigvis, det kan være normalt at der ikke er data endnu
        // setError("Ingen opkaldsdata fundet."); 
      }
    });

    // Cleanup funktion
    return () => {
      unsubscribe();
    };
  }, [currentUser]); // Dependency array inkluderer currentUser

  // Funktion til at processere det seneste opkald og opdatere state
  const processAndSetDashboardData = (callData: CallData | null, userData: UserMetadata | null) => {
    if (!callData) { // Forenklet tjek: hvis ingen callData, nulstil alt
      setDominanceDistance(null);
      setPitchStdDistance(null);
      setEnergyStdDistance(null);
      setTlrDistance(null);
      setDominanceMean(null);
      setPitchStd(null);
      setEnergyStd(null);
      setSpeechRateMean(null);
      setTlrValue(null);
      setFeedbackText(t('agentDashboard.noDataAvailable'));
      setActionPunkt1('');
      setActionPunkt2('');
      setActionPunkt3Observed('');
      setActionPunkt4Observed('');
      // Nulstil spørgsmålsdata
      setHvQuestions([]);
      setHvQuestionsCount(0);
      setPartialAccepts([]);
      setPartialAcceptsCount(0);
      return;
    }

    const obsValues = callData.observed_values;
    const benchmarkDist = callData.benchmark_distances; // Hent de specifikke benchmark afstande

    // Opdater state for Nøgletal (Key Metrics) primært fra benchmark_distances
    setDominanceDistance(benchmarkDist?.dominance_distance ?? null);
    setPitchStdDistance(benchmarkDist?.pitch_std_distance ?? null);
    setEnergyStdDistance(benchmarkDist?.energy_std_distance ?? null); // Antager denne også kommer fra benchmark
    setTlrDistance(benchmarkDist?.tlr_distance ?? null);

    // Opdater andre state-variabler som før (fra observed_values eller direkte fra callData)
    if (obsValues) {
      setDominanceMean(obsValues.dominance_mean ?? null);
      setPitchStd(obsValues.pitch_std ?? null);
      setEnergyStd(obsValues.energy_std ?? null);
      setSpeechRateMean(obsValues.speech_rate_mean ?? null);
      // TlrValue kan komme fra callData.tlr (som kan være fra observed_values.tlr i servicen)
      setTlrValue(callData.tlr ?? obsValues.tlr ?? null);
    } else {
        // Nulstil observed_values-afhængige states hvis observed_values mangler
        setDominanceMean(null);
        setPitchStd(null);
        setEnergyStd(null);
        setSpeechRateMean(null);
        setTlrValue(callData.tlr ?? null); // Forsøg stadig at sætte tlr fra top-niveau hvis obsValues mangler
    }

    setFeedbackText(callData.feedback_text || t('agentDashboard.feedbackUnavailable'));
    setActionPunkt1(callData.action_punkt_1 || '');
    setActionPunkt2(callData.action_punkt_2 || '');
    setActionPunkt3Observed(callData.action_punkt_3_observed_values || '');
    setActionPunkt4Observed(callData.action_punkt_4_observed_values || '');

    // Opdater spørgsmålsdata fra transcription_reactions
    const transcriptionReactions = callData.transcription_reactions;
    if (transcriptionReactions) {
      // HV Questions
      if (transcriptionReactions.hv_questions) {
        setHvQuestions(transcriptionReactions.hv_questions.examples || []);
        setHvQuestionsCount(transcriptionReactions.hv_questions.total_count || 0);
      } else {
        setHvQuestions([]);
        setHvQuestionsCount(0);
      }

      // Partial Accepts
      if (transcriptionReactions.partial_accepts) {
        setPartialAccepts(transcriptionReactions.partial_accepts.examples || []);
        setPartialAcceptsCount(transcriptionReactions.partial_accepts.total_count || 0);
      } else {
        setPartialAccepts([]);
        setPartialAcceptsCount(0);
      }
    } else {
      // Nulstil hvis transcription_reactions mangler
      setHvQuestions([]);
      setHvQuestionsCount(0);
      setPartialAccepts([]);
      setPartialAcceptsCount(0);
    }

    setDebugInfo({ processedCallId: callData.id, timestamp: new Date().toLocaleTimeString(), userContextProvided: !!userData, benchmarkDistancesAvailable: !!benchmarkDist });
  };

  // Beregn fyldningsprocent baseret på afstand (generel funktion for alle metrics)
  const calculateFillPercentage = (distance: number | null, metric: string = 'default') => {
    if (distance === null) return 0;
    
    // Forskellige beregninger afhængigt af metrik-type
    if (metric === 'pitch_std') {
      // Særlig skala for pitch_std_distance: 0 = 100% fyldt, 51,361 = 0% fyldt
      return Math.max(0, Math.min(100, (1 - distance / 51.361) * 100));
    } else if (metric === 'dominance') {
      // Opdateret skala for dominans: 0 = 100% fyldt, 1,5293 = 0% fyldt
      return Math.max(0, Math.min(100, (1 - distance / 1.5293) * 100));
    } else if (metric === 'energy_std') {
      // Skala for energi variation: 0 = 100% fyldt, 1.0 = 0% fyldt
      return Math.max(0, Math.min(100, (1 - distance / 1.0) * 100));
    } else if (metric === 'tlr') {
      // Skala for TLR distance: 0 = 100% fyldt, 0,625 = 0% fyldt
      return Math.max(0, Math.min(100, (1 - distance / 0.625) * 100));
    } else {
      // Standard skala for andre metrikker: 0 = 100% fyldt, 2 = 0% fyldt
      return Math.max(0, Math.min(100, (1 - distance / 2) * 100));
    }
  };

  // Beskriv dominansafstanden med ord
  const getDominanceLabel = (distance: number | null) => {
    if (distance === null) return "Ikke tilgængelig";
    if (distance < 0.5) return "Meget dominerende";
    if (distance < 1.0) return "Moderat dominerende";
    if (distance < 1.5) return "Let dominerende";
    return "Ikke dominerende";
  };

  // Beskriv pitch-variationen med ord
  const getPitchStdLabel = (distance: number | null) => {
    if (distance === null) return "Ikke tilgængelig";
    if (distance < 0.5) return "Meget dynamisk tonehøjde";
    if (distance < 1.0) return "God variation i tonehøjde";
    if (distance < 1.5) return "Moderat variation i tonehøjde";
    return "Monoton - kunne bruge mere variation";
  };

  // Beskriv energi-variationen med ord
  const getEnergyStdLabel = (distance: number | null) => {
    if (distance === null) return "Ikke tilgængelig";
    if (distance < 0.5) return "Meget variabel energi";
    if (distance < 1.0) return "Moderat variabel energi";
    if (distance < 1.5) return "Let variabel energi";
    return "Ikke variabel energi";
  };

  // Beskriv TLR distancen med ord
  const getTlrDistanceLabel = (distance: number | null) => {
    if (distance === null) return "Ikke tilgængelig";
    if (distance < 0.15) return "Optimal samtalebalance";
    if (distance < 0.3) return "God samtalebalance";
    if (distance < 0.45) return "Acceptabel samtalebalance";
    return "Justér din tale/lytte-ratio";
  };

  // Funktion til at beskrive tlr-værdien
  const getTlrDescription = (tlr: number | null): string => {
    if (tlr === null) return "Ingen data tilgængelig";
    if (tlr < 0.4) return "Du lytter meget mere end du taler";
    if (tlr < 0.8) return "Du har en balanceret samtale med god lytning";
    if (tlr < 1.2) return "Du taler og lytter i omtrent lige stort omfang";
    if (tlr < 2.0) return "Du taler noget mere end du lytter";
    return "Du dominerer samtalen med meget tale";
  };

  const formatFeedbackText = (text: string): string => {
    if (!text) return "";
    
    // Fjern eventuelle ekstra linjeskift
    let formatted = text.replace(/\n+/g, ' ');
    
    // Reducer flere mellemrum til ét
    formatted = formatted.replace(/\s+/g, ' ');
    
    // Vi viser hele teksten nu, så vi behøver ikke begrænse den
    // const sentences = formatted.match(/[^.!?]+[.!?]+/g) || [];
    // if (sentences.length > 3) {
    //   formatted = sentences.slice(0, 3).join(' ');
    // }
    
    return formatted.trim();
  };

  const handleNavigate = (path: string) => {
    console.log(`Navigerer til: ${path}`); // Debugging log
    if (path === 'Race') {
      navigate('/race');
    } else if (path === 'Calls') {
      navigate('/calls');
    } else if (path === 'Dashboard') {
      navigate('/agent-dashboard');
    } else if (path === 'Stats') {
      navigate('/stats');
    } else if (path === 'Training') {
      navigate('/training');
    } else if (path === 'Development' || path === 'Team' || path === 'Help' || path === 'Contact') {
      // Disse sider er endnu ikke implementeret
      console.log(`Side '${path}' er ikke implementeret endnu`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <LoadingScreen isVisible={true} inline={true} />
      </Box>
    );
  }

  // Beregn fyldningsprocenter for hver metrik
  const dominanceFillPercentage = calculateFillPercentage(dominanceDistance, 'dominance');
  const pitchStdFillPercentage = calculateFillPercentage(pitchStdDistance, 'pitch_std');
  const energyStdFillPercentage = calculateFillPercentage(energyStdDistance, 'energy_std');
  const tlrFillPercentage = calculateFillPercentage(tlrDistance, 'tlr');

  // Maksimale afstandsværdier
  const DOMINANCE_MAX = 1.5293;
  const PITCH_STD_MAX = 51.361;
  const ENERGY_STD_MAX = 1.0;
  const TLR_MAX = 0.625;
  
  // Log data til debugging
  console.log("Data der sendes til Hexagon chart:", {
    dominanceMean, pitchStd, speechRateMean, energyStd,
    maxValues: { dominanceMax: 1, pitchStdMax: 80, speechRateMax: 0.6, energyStdMax: 15 }
  });

  // Yderligere debug-log lige før rendering
  console.log("FINAL DATA til Hexagon chart:", {
    dominanceMean: dominanceMean === null ? "NULL" : dominanceMean,
    pitchStd: pitchStd === null ? "NULL" : pitchStd,
    speechRateMean: speechRateMean === null ? "NULL" : speechRateMean,
    energyStd: energyStd === null ? "NULL" : energyStd,
    erVærdierNull: {
      dominanceMean: dominanceMean === null,
      pitchStd: pitchStd === null,
      speechRateMean: speechRateMean === null,
      energyStd: energyStd === null
    }
  });

  return (
    <AppContainer>
      {/* Sidebar / Menu */}
      <Sidebar>
        <Box sx={{ padding: '10px', marginBottom: 2 }}>
          <ProfileImageUpload />
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Dashboard')} sx={{ marginTop: 1, backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
          <MenuItemText>Dashboard</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Development</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Stats')}>
          <MenuItemText>Stats</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Calls')}>
          <MenuItemText>Calls</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Team</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Race')}>
          <MenuItemText>Race</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Training')}>
          <MenuItemText>{t('common.training')}</MenuItemText>
        </MenuItemContainer>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItemContainer onClick={() => handleNavigate('Help')}>
          <MenuItemText>Help</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Contact')}>
          <MenuItemText>Contact</MenuItemText>
        </MenuItemContainer>
      </Sidebar>
      
      {/* Main Content */}
      <MainContent>
        {/* Header */}
        <Header>
          <Typography variant="h6">{userName || "Voice Victor"}</Typography>
        </Header>
        
        {/* Dashboard Content */}
        <ContentWrapper>
          <DashboardBackground />
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              {error}
            </Alert>
          )}
          
          {/* Feedback Container - High priority */}
          <FeedbackContainer>
            <PatternOverlay />
            <GlowEffect />
            <FeedbackText>
              {feedbackText || "Ingen feedback tilgængelig"}
            </FeedbackText>
          </FeedbackContainer>
          
          {/* Voice Analysis Top Section */}
          <Section>
            <Grid container spacing={3}>
              {/* Voice Score & Tone Patterns */}
              <Grid item xs={12} md={5}>
                <AnalysisCard>
                  <BorderHighlight />
                  <HeadingWithIcon>
                    <RecordVoiceOverIcon />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      Action
                    </Typography>
                  </HeadingWithIcon>
                  
                  <HighlightBox>
                    <Box sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}>
                      <Box sx={{ width: '12px', height: '12px', bgcolor: '#1976d2', mr: 1.5, borderRadius: '2px' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {actionPunkt1 || "Din stemme"}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '12px', height: '12px', bgcolor: '#90caf9', mr: 1.5, borderRadius: '2px' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {actionPunkt2 || "Benchmark"}
                      </Typography>
                    </Box>
                  </HighlightBox>
                  
                  <Box sx={{ mt: 3, mb: 1 }}>
                    <CardHeading>
                      {t('dashboard.developmentOverTime')}
                    </CardHeading>
                  </Box>
                  
                  <Box sx={{ height: '180px', width: '100%' }}>
                    {voiceScoreHistory.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={voiceScoreHistory}
                          margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11, fill: '#666' }}
                            tickFormatter={(value) => {
                              if (!value) return '';
                              return value;
                            }}
                            stroke="rgba(0,0,0,0.08)"
                            padding={{ left: 10, right: 10 }}
                          />
                          <YAxis 
                            domain={[0, 100]}
                            tick={{ fontSize: 11, fill: '#666' }} 
                            stroke="rgba(0,0,0,0.08)"
                          />
                          <Tooltip 
                            formatter={(value) => [`${value}`, 'Score']}
                            labelFormatter={(label) => `Dato: ${label}`}
                            contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="score" 
                            stroke="#1976d2" 
                            strokeWidth={2.5}
                            dot={{ r: 4, strokeWidth: 1, fill: "#1976d2" }}
                            activeDot={{ r: 6, stroke: "#1976d2", strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.9rem' }}>
                          Ingen data tilgængelig
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AnalysisCard>
              </Grid>
              
              {/* Key Metrics */}
              <Grid item xs={12} md={7}>
                <AnalysisCard>
                  <BorderHighlight />
                  <HeadingWithIcon>
                    <EqualizerIcon />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {t('dashboard.keyMetrics')}
                    </Typography>
                  </HeadingWithIcon>
                  
                  {/* Dominance Metric */}
                  <StatRowContainer>
                    <StatCircleIcon>
                      <MicIcon />
                    </StatCircleIcon>
                    <StatisticsContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#334155' }}>
                          {t('dashboard.authority')}
                        </Typography>
                        <PercentIndicator>{dominanceFillPercentage.toFixed(0)}%</PercentIndicator>
                      </Box>
                      <BarContainer>
                        {dominanceDistance !== null ? (
                          <BarFill fillpercentage={dominanceFillPercentage} />
                        ) : (
                          <BarFill fillpercentage={0} />
                        )}
                      </BarContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', mt: 1 }}>
                        <span>{getDominanceLabel(dominanceDistance)}</span>
                      </Box>
                    </StatisticsContent>
                  </StatRowContainer>
                  
                  {/* Pitch Variation Metric */}
                  <StatRowContainer>
                    <StatCircleIcon>
                      <GraphicEqIcon />
                    </StatCircleIcon>
                    <StatisticsContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#334155' }}>
                          {t('dashboard.pitchVariation')}
                        </Typography>
                        <PercentIndicator>{pitchStdFillPercentage.toFixed(0)}%</PercentIndicator>
                      </Box>
                      <BarContainer>
                        {pitchStdDistance !== null ? (
                          <BarFill fillpercentage={pitchStdFillPercentage} />
                        ) : (
                          <BarFill fillpercentage={0} />
                        )}
                      </BarContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', mt: 1 }}>
                        <span>{getPitchStdLabel(pitchStdDistance)}</span>
                      </Box>
                    </StatisticsContent>
                  </StatRowContainer>
                  
                  {/* Talk-Listen Ratio (TLR) Metric (tidligere Energy Std) */}
                  <StatRowContainer>
                    <StatCircleIcon>
                      <SpeedIcon />
                    </StatCircleIcon>
                    <StatisticsContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#334155' }}>
                          {t('dashboard.talkRatio')}
                        </Typography>
                        <PercentIndicator>{tlrFillPercentage.toFixed(0)}%</PercentIndicator>
                      </Box>
                      <BarContainer>
                        {tlrDistance !== null ? (
                          <BarFill fillpercentage={tlrFillPercentage} />
                        ) : (
                          <BarFill fillpercentage={0} />
                        )}
                      </BarContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', mt: 1 }}>
                        <span>{getTlrDistanceLabel(tlrDistance)}</span>
                      </Box>
                    </StatisticsContent>
                  </StatRowContainer>
                </AnalysisCard>
              </Grid>
            </Grid>
          </Section>
          
          {/* Second Row - Profile & Improvements */}
          <Section>
            <SectionTitle>{t('dashboard.insightAndImprovement')}</SectionTitle>
            
            <Grid container spacing={3}>
              {/* Voice Profile Radar Chart - Behold original size */}
              <Grid item xs={12} md={7}>
                <AnalysisCard>
                  <BorderHighlight />
                  <HeadingWithIcon>
                    <TrendingUpIcon />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {t('dashboard.yourVoiceProfile')}
                    </Typography>
                  </HeadingWithIcon>
                  
                  <RadarContainer>
                    <Radar
                      data={{
                        labels: [
                          t('dashboard.authority'),
                          t('dashboard.pitchVariation'),
                          t('dashboard.speechRate')
                        ],
                        datasets: [
                          {
                            label: 'Egenskaber',
                            data: [
                              dominanceMean !== null ? Math.min(Math.abs(Number(dominanceMean)) / 0.8, 1) : 0.65,
                              pitchStd !== null ? Math.min(Number(pitchStd) / 70.0, 1) : 0.78,
                              energyStd !== null ? Math.min(Number(energyStd) / 15.0, 1) : 0.72
                            ],
                            backgroundColor: 'rgba(63, 81, 181, 0.15)',
                            borderColor: 'rgba(63, 81, 181, 0.8)',
                            borderWidth: 2.5,
                            pointBackgroundColor: '#3f51b5',
                            pointBorderColor: '#fff',
                            pointHoverBackgroundColor: '#fff',
                            pointHoverBorderColor: '#3f51b5',
                            pointBorderWidth: 2,
                          }
                        ]
                      }}
                      options={{
                        scales: {
                          r: {
                            min: 0,
                            max: 1,
                            ticks: {
                              stepSize: 0.2,
                              backdropColor: 'rgba(0, 0, 0, 0)',
                              display: false
                            },
                            pointLabels: {
                              font: {
                                family: "'Poppins', sans-serif",
                                size: 16,
                                weight: 600
                              },
                              color: '#334155',
                              padding: 10
                            },
                            grid: {
                              circular: true,
                              color: (context) => {
                                const index = context.index;
                                const alpha = 0.03 + (index * 0.02);
                                return `rgba(63, 81, 181, ${alpha})`;
                              },
                              lineWidth: (context) => {
                                const index = context.index;
                                return index === 0 ? 0 : index === 5 ? 2 : 1;
                              }
                            },
                            angleLines: {
                              lineWidth: 1.5,
                              color: 'rgba(63, 81, 181, 0.08)'
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            backgroundColor: 'rgba(255, 255, 255, 0.97)',
                            titleColor: '#334155',
                            bodyColor: '#334155',
                            bodyFont: {
                              family: "'Poppins', sans-serif",
                              size: 14
                            },
                            titleFont: {
                              family: "'Poppins', sans-serif",
                              size: 16,
                              weight: 'bold'
                            },
                            padding: 15,
                            cornerRadius: 10,
                            borderColor: 'rgba(63, 81, 181, 0.2)',
                            borderWidth: 1,
                            callbacks: {
                              title: function(tooltipItems) {
                                return tooltipItems[0].label;
                              },
                              label: function(context) {
                                // Returnerer en tom string for at fjerne værdi-visningen
                                return '';
                              }
                            }
                          }
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        elements: {
                          line: {
                            tension: 0.2,
                            borderWidth: 3
                          },
                          point: {
                            radius: 6,
                            hoverRadius: 10
                          }
                        },
                        animation: {
                          duration: 1500,
                          easing: 'easeOutQuart'
                        }
                      }}
                    />
                  </RadarContainer>
                </AnalysisCard>
              </Grid>
              
              {/* Side panel with two smaller cards */}
              <Grid item xs={12} md={5}>
                <Grid container spacing={3} direction="column">
                  {/* Improvement Opportunities - Reduced Size */}
                  <Grid item>
                    <AnalysisCard sx={{ height: 'auto', position: 'relative' }}>
                      <BorderHighlight />
                      {/* Redesignet overlay for bedre blend og eksklusivitet */}
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        background: 'linear-gradient(rgba(255, 255, 255, 0.85), rgba(240, 242, 245, 0.85))',
                        borderRadius: '12px',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.12)',
                        overflow: 'hidden',
                      }}>
                        {/* Subtil baggrundsgrafik */}
                        <Box sx={{
                          position: 'absolute',
                          top: 0, right: 0, bottom: 0, left: 0,
                          backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 70%)',
                          opacity: 0.8,
                          zIndex: -1,
                        }} />
                        
                        {/* Premium låsesymbol med cirkel omkring */}
                        <Box sx={{ 
                          width: '50px', 
                          height: '50px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(25, 118, 210, 0.06)',
                          border: '1px solid rgba(25, 118, 210, 0.15)',
                          mb: 2,
                        }}>
                          <LockIcon sx={{ 
                            fontSize: '1.5rem', 
                            color: 'rgba(25, 118, 210, 0.6)',
                          }} />
                        </Box>
                        
                        {/* Mere sofistikeret "Kommer snart" tekst */}
                        <Typography variant="subtitle1" sx={{ 
                          color: 'rgba(25, 118, 210, 0.8)', 
                          fontWeight: 500,
                          letterSpacing: '0.5px',
                        }}>
                          {t('dashboard.comingSoon')}
                        </Typography>
                        
                        {/* Subtil underline */}
                        <Box sx={{ 
                          width: '40px', 
                          height: '2px', 
                          background: 'rgba(25, 118, 210, 0.2)', 
                          my: 1.5 
                        }} />
                        
                        {/* Beskrivende tekst */}
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(0, 0, 0, 0.45)', 
                          maxWidth: '70%', 
                          textAlign: 'center',
                          fontSize: '0.75rem',
                        }}>
                          {t('dashboard.featureNotAvailable')}
                        </Typography>
                      </Box>
                      <HeadingWithIcon>
                        <TrendingUpIcon />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                          {t('dashboard.improvementOpportunities')}
                        </Typography>
                      </HeadingWithIcon>
                      
                      <Box sx={{ mt: 1 }}>
                        <SampleItem>
                          <PlayButton size="small">
                            <PlayArrowIcon fontSize="small" />
                          </PlayButton>
                          <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.85rem', fontWeight: 500, ml: 1.5, color: '#334155' }}>
                            {t('dashboard.best30Seconds')}
                          </Typography>
                          <IconButton size="small" color="primary">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </SampleItem>
                        
                        <SampleItem>
                          <PlayButton size="small">
                            <PlayArrowIcon fontSize="small" />
                          </PlayButton>
                          <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.85rem', fontWeight: 500, ml: 1.5, color: '#334155' }}>
                            {t('dashboard.worst30Seconds')}
                          </Typography>
                          <IconButton size="small" color="primary">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </SampleItem>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <SeeMoreButton>
                            {t('dashboard.seeAllImprovements')} <ArrowForwardIcon />
                          </SeeMoreButton>
                        </Box>
                      </Box>
                    </AnalysisCard>
                  </Grid>
                  
                  {/* Talk to Listen Ratio Box (replacing empty placeholder) */}
                  <Grid item>
                    <TalkListenRatioBox>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <RatioLabel>
                          <RecordVoiceOverIcon />
                          Talk ratio
                        </RatioLabel>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2, position: 'relative', top: '15px' }}>
                        <RatioValue>
                          {tlrValue !== null ? tlrValue.toFixed(2) : 'N/A'}
                        </RatioValue>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.9rem', ml: 1, mb: 0.8 }}>
                          TR
                        </Typography>
                      </Box>

                      <RatioMeter ratio={tlrValue !== null ? tlrValue : 0} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', mt: 1 }}>
                        <span>{t('dashboard.moreListening')}</span>
                        <span>{t('dashboard.moreTalking')}</span>
                      </Box>
                    </TalkListenRatioBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>

          {/* New Section */}
          <Section>
            <SectionTitle>{t('questions.sectionTitle')}</SectionTitle>
            <Grid container spacing={3}>
              {/* HV-Questions Card */}
              <Grid item xs={12} md={6}>
                <AnalysisCard sx={{ position: 'relative' }}>
                  <BorderHighlight />
                  <CountBadge bgcolor="#1976d2">{hvQuestionsCount}</CountBadge>
                  <HeadingWithIcon>
                    <FlagOutlinedIcon />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {t('questions.hvQuestions')}
                    </Typography>
                  </HeadingWithIcon>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    {t('questions.hvQuestionsDescription')}
                  </Typography>

                  {hvQuestions.slice(0, 5).map((q, idx) => (
                    <QuestionRow key={idx} bgcolor="rgba(25,118,210,0.05)">
                      <NumberBubble bgcolor="#1976d2">{idx + 1}</NumberBubble>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {q}
                      </Typography>
                    </QuestionRow>
                  ))}

                  <MoreLink>+{Math.max(0, hvQuestionsCount - 5)} {t('questions.moreQuestions')}</MoreLink>
                </AnalysisCard>
              </Grid>

              {/* Partial Accepts Card */}
              <Grid item xs={12} md={6}>
                <AnalysisCard sx={{ position: 'relative' }}>
                  <BorderHighlight />
                  <CountBadge bgcolor="#9c27b0">{partialAcceptsCount}</CountBadge>
                  <HeadingWithIcon>
                    <FormatListNumberedOutlinedIcon />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {t('questions.partialAccepts')}
                    </Typography>
                  </HeadingWithIcon>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    {t('questions.partialAcceptsDescription')}
                  </Typography>

                  {partialAccepts.slice(0, 5).map((q, idx) => (
                    <QuestionRow key={idx} bgcolor="rgba(156,39,176,0.05)">
                      <NumberBubble bgcolor="#9c27b0">{idx + 1}</NumberBubble>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {q}
                      </Typography>
                    </QuestionRow>
                  ))}

                  <MoreLink>+{Math.max(0, partialAcceptsCount - 5)} {t('questions.moreQuestions')}</MoreLink>
                </AnalysisCard>
              </Grid>
            </Grid>
          </Section>
          
          {/* Debug Information (Hidden in Production) */}
          <Box mt={4} sx={{ display: process.env.NODE_ENV === 'development' ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>
              Debug Information:
            </Typography>
            
            <pre style={{ backgroundColor: '#eee', padding: '1rem', overflow: 'auto', borderRadius: '4px' }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </Box>
        </ContentWrapper>
      </MainContent>
    </AppContainer>
  );
};

export default AgentDashboard; 